import { Icon, Typography } from '@sanlam-glacier/app-common/components';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getEditProposalInformation, IAPPROPOSALSTATUS, SecurityAccessFeaturesEnum } from 'bff';
import { HorizontalStepper } from 'components/molecules/horizontal-stepper/HorizontalStepper';
import { useGetProposalIdFromURL } from 'hooks/useGetProposalIdFromURL';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useUserHasFeature } from 'hooks/useUserHasFeature';
import { useConfig } from 'providers/config/ConfigProvider';
import { useNavigate } from 'react-router-dom';
import { routes } from 'routes/route-paths';
import { StepConfig, StepperCategoryState } from 'types/stepper/proposal-stepper-type';
import { GoToStepProps, StepNumber } from 'types/stepper/stepper.type';
import { getAllowedSteps } from 'modules/new-business/utils/proposal-form-state/proposal-form-state.function';
import { proposalStepToRoute } from 'modules/new-business/utils/proposal-stepper/proposal-route-mapping';
import { getStepConfig } from 'modules/new-business/utils/proposal-stepper/step-configuration';
import { getProposalLockedFlags } from 'modules/new-business/utils/proposal/is-proposal-locked.function';
import { isCurrentRoute } from '@sanlam-glacier/app-common/utils';
import styles from './proposal-stepper.module.scss';

interface ProposalStepperProps {
  currentStep: StepNumber;
}

export const ProposalStepper = ({ currentStep }: ProposalStepperProps) => {
  const proposalId = useGetProposalIdFromURL();
  const navigate = useNavigate();
  const isSpecialQuoteUser = useUserHasFeature(SecurityAccessFeaturesEnum.ProposalOverride);

  const {
    featureFlags: { stepperFeatureToggle }
  } = useConfig();
  const [overrideStepper] = useLocalStorage('overrideStepper', stepperFeatureToggle);

  const {
    data: { isProposalLocked, cloneReason, overrides, status, selectedScenarioId, stateModel }
  } = useSuspenseQuery(getEditProposalInformation({ proposalId }));

  const { isProposalClonedForOverride, isProposalClonedForRequote } = getProposalLockedFlags({
    isProposalLocked,
    overrides,
    isSpecialQuoteUser,
    cloneReason
  });

  const stepConfig = getStepConfig(
    stateModel!,
    isProposalClonedForOverride || isProposalClonedForRequote,
    status,
    overrideStepper
  );

  const getSigningProgressRoute = () => {
    return isCurrentRoute(routes.proposalsRoutes.editRoutes.signingSummary);
  };

  const getStepIcon = (step: StepConfig): React.ReactNode => {
    const isStepSix = step.step === 6;
    const isSigningComplete = status === IAPPROPOSALSTATUS.SigningComplete;
    const isSigningProgress = status === IAPPROPOSALSTATUS.SigningInProgress;
    const isOnSigningProgressRoute = getSigningProgressRoute();

    const isStepLocked =
      (isProposalLocked && !isStepSix) ||
      (isStepSix && isSigningComplete) ||
      (isStepSix && isSigningProgress && !isOnSigningProgressRoute) ||
      isProposalClonedForRequote ||
      isProposalClonedForOverride;
    const isCurrentStep = step.step === currentStep;
    const stepNumberIcon = (
      <Typography color={Typography.COLOR.INHERIT} weight={Typography.WEIGHT.MEDIUM}>
        {step.step}
      </Typography>
    );

    if (isCurrentStep) return stepNumberIcon;
    if (isStepLocked) return <Icon icon={'action_lock'} />;
    if (step.state === StepperCategoryState.ERROR)
      return <Icon icon={'communication_priority-high'} />;
    if (step.state === StepperCategoryState.COMPLETED) return <Icon icon={'action_done'} />;
    return stepNumberIcon;
  };

  const getMaxAllowedFormStep = (max: StepNumber, selectedScenarioId?: number) =>
    !!selectedScenarioId && max > 3 ? 3 : max;

  const isStepDisabled =
    (isClonedProposal: boolean) =>
    (step: StepNumber): boolean => {
      if (overrideStepper) {
        return false;
      }

      const maxAllowedStep = getMaxAllowedFormStep(
        getAllowedSteps(stateModel!),
        selectedScenarioId
      );

      // Enable steps we've completed
      if (step <= maxAllowedStep) {
        return false;
      }

      if (step > maxAllowedStep && isClonedProposal) {
        return false;
      }

      // Disable all other steps
      return true;
    };

  const goToStep = ({ step }: GoToStepProps) => {
    if (step !== currentStep) {
      const targetRoute = proposalStepToRoute(step);
      navigate(targetRoute);
    }
  };

  return (
    <div className={styles.proposalStepperContainer}>
      <HorizontalStepper
        stepConfig={stepConfig}
        currentStep={currentStep}
        goToStep={goToStep}
        getStepIcon={getStepIcon}
        isStepDisabled={isStepDisabled(isProposalClonedForOverride || isProposalClonedForRequote)}
      />
    </div>
  );
};
